import { DOCUMENT } from '@angular/common';
import {
  afterNextRender,
  afterRenderEffect,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  viewChild,
} from '@angular/core';
import { MobiUiIconComponent } from '@mobi/rwc-ui-components-ng-jslib';
import { DrupalService } from '@app/services/drupal.service';
import { PlatformContextService } from '@app/services/platform-context.service';
import { ComponentSharedModule } from '@app/modules/component.shared.module';
import { MobiLdSchemaComponent } from '@app/components/mobi-ld-schema/mobi-ld-schema.component';

@Component({
  selector: 'mobi-breadcrumb',
  templateUrl: './breadcrumb.component.html',
  styleUrls: ['./breadcrumb.component.scss'],
  imports: [ComponentSharedModule, MobiUiIconComponent, MobiLdSchemaComponent],
  standalone: true,
})
export class BreadcrumbComponent {
  #drupal = inject(DrupalService);
  #doc = inject(DOCUMENT);
  platformService = inject(PlatformContextService);
  readonly nav = viewChild<ElementRef<HTMLElement>>('nav');
  readonly list = viewChild<ElementRef<HTMLOListElement>>('list');
  #resizeObserver?: ResizeObserver;
  #observed?: HTMLElement;

  breadcrumb = this.#drupal.breadcrumb;

  schema = computed(() => {
    const { protocol, hostname } = this.#doc.location;
    const origin = `${protocol}//${hostname}`;
    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: this.breadcrumb()?.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: index === 0 ? 'Home' : item.title,
        item: origin + item.url.path,
      })),
    };
  });

  constructor() {
    afterRenderEffect(() => {
      this.breadcrumb();
      this.#observe(this.nav()?.nativeElement);
      this.#layout();
    });
    afterNextRender(() => {
      this.#doc.fonts?.ready.then(() => this.#layout());
    });
    inject(DestroyRef).onDestroy(() => this.#resizeObserver?.disconnect());
  }

  #observe(nav?: HTMLElement): void {
    if (nav === this.#observed || typeof ResizeObserver === 'undefined') return;
    this.#resizeObserver?.disconnect();
    this.#observed = nav;
    if (!nav) return;
    this.#resizeObserver = new ResizeObserver(() => this.#layout());
    this.#resizeObserver.observe(nav);
  }

  // Collapses ancestors into "…" from the home side until the trail fits on one line;
  // without is-trail mobi-ui falls back to its single parent item.
  #layout(): void {
    const nav = this.nav()?.nativeElement;
    const list = this.list()?.nativeElement;
    if (!nav || !list) return;

    const ancestors = Array.from(list.children).slice(1, -1);
    const overflows = () => list.scrollWidth > list.clientWidth;

    nav.classList.add('is-trail');
    ancestors.forEach((li) => li.classList.remove('is-collapsed'));

    for (const li of ancestors) {
      if (!overflows()) return;
      li.classList.add('is-collapsed');
    }
    if (!overflows()) return;

    ancestors.forEach((li) => li.classList.remove('is-collapsed'));
    nav.classList.remove('is-trail');
  }
}
